"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MessageType = exports.ProviderLimits = exports.CausalHistoryProvider = void 0;
const CausalHistoryDelta_1 = require("../../../../data/history/CausalHistoryDelta");
const ObjectPacker_1 = require("../../../../data/packing/ObjectPacker");
const logging_1 = require("../../../../util/logging");
const CausalHistorySyncAgent_1 = require("../CausalHistorySyncAgent");
var MessageType;
(function (MessageType) {
    MessageType["Request"] = "request";
    MessageType["Response"] = "response";
    MessageType["RejectRequest"] = "reject-request";
    MessageType["SendLiteral"] = "send-literal";
    MessageType["CancelRequest"] = "cancel-request";
})(MessageType || (MessageType = {}));
exports.MessageType = MessageType;
;
const ProviderLimits = {
    MaxOpsToRequest: 128,
    MaxLiteralsPerResponse: 1024,
    MaxHistoryPerResponse: 256
};
exports.ProviderLimits = ProviderLimits;
const LiteralBatchSize = 256;
class CausalHistoryProvider {
    constructor(syncAgent) {
        this.syncAgent = syncAgent;
        this.responses = new Map();
        this.currentResponses = new Map();
        this.queuedResponses = new Map();
        this.streamingResponses = 0;
        this.checkIfLiteralIsValidOp = (literal) => this.syncAgent.literalIsValidOp(literal);
        this.continueStreamingResponses = this.continueStreamingResponses.bind(this);
        this.controlLog = CausalHistoryProvider.controlLog;
        this.storeLog = CausalHistoryProvider.storeLog;
        this.opXferLog = CausalHistoryProvider.opXferLog;
    }
    continueStreamingResponses() {
        for (const requesId of this.currentResponses.values()) {
            const respInfo = this.responses.get(requesId);
            if (respInfo !== undefined) {
                this.sendLiterals(respInfo.request.requestId, LiteralBatchSize);
            }
        }
    }
    // TODO: check if we answer right away, or if we're already streaming literals
    //       from a previous request and this needs to be queued
    async onReceivingRequest(remote, msg) {
        if (this.responses.get(msg.requestId) === undefined) {
            const respInfo = {
                request: msg,
                remote: remote,
                status: 'created',
                requestArrivalTimestamp: Date.now()
            };
            if (msg.mutableObj !== this.syncAgent.mutableObj) {
                const detail = 'Rejecting request ' + respInfo.request.requestId + ', mutableObj is ' + respInfo.request.mutableObj + ' but it should be ' + this.syncAgent.mutableObj;
                this.rejectRequest(respInfo, 'invalid-request', detail);
                return;
            }
            else {
                this.responses.set(msg.requestId, respInfo);
                if (this.currentResponses.get(remote) === undefined) {
                    this.sendResponse(respInfo);
                }
                else {
                    this.enqueueResponse(respInfo);
                }
            }
        }
    }
    //TODO
    onReceivingRequestCancellation(remote, msg) {
        remote;
        msg;
    }
    async createResponse(respInfo) {
        await this.logStoreContents(respInfo.request.requestId);
        const req = respInfo.request;
        const resp = {
            type: MessageType.Response,
            requestId: respInfo.request.requestId,
            literalCount: 0
        };
        // Validate history request, if present
        if (respInfo.request.requestedTerminalOpHistory !== undefined || respInfo.request.requestedStartingOpHistory !== undefined) {
            const toCheck = new Set();
            if (respInfo.request.requestedTerminalOpHistory !== undefined) {
                for (const hash of respInfo.request.requestedTerminalOpHistory) {
                    toCheck.add(hash);
                }
            }
            if (respInfo.request.requestedStartingOpHistory !== undefined) {
                for (const hash of respInfo.request.requestedStartingOpHistory) {
                    toCheck.add(hash);
                }
            }
            for (const opHistoryHash of toCheck) {
                const opHistory = await this.syncAgent.store.loadOpCausalHistoryByHash(opHistoryHash);
                if (opHistory !== undefined) {
                    const literal = await this.syncAgent.store.loadLiteral(opHistory.opHash);
                    if (!this.syncAgent.literalIsValidOp(literal)) {
                        const detail = 'Invalid requestedTerminalOpHistory/requestedStartingOpHistory for request ' + respInfo.request.requestId + ', rejecting';
                        this.rejectRequest(respInfo, 'invalid-request', detail);
                        return false;
                    }
                }
            }
        }
        // Validate requested ops, if present
        if (respInfo.request.requestedOps !== undefined) {
            for (const opHash of respInfo.request.requestedOps) {
                const literal = await this.syncAgent.store.loadLiteral(opHash);
                if (!this.syncAgent.literalIsValidOp(literal)) {
                    const detail = 'Invalid requestedOps for request ' + respInfo.request.requestId + ', rejecting';
                    this.rejectRequest(respInfo, 'invalid-request', detail);
                    return false;
                }
            }
        }
        // Validate sent state, if present
        const remoteStateOps = new Set();
        if (respInfo.request.currentState !== undefined) {
            for (const opHistoryHash of respInfo.request.currentState) {
                const opHistory = await this.syncAgent.store.loadOpCausalHistoryByHash(opHistoryHash);
                if (opHistory !== undefined) {
                    const literal = await this.syncAgent.store.loadLiteral(opHistory.opHash);
                    if (!this.syncAgent.literalIsValidOp(literal)) {
                        const detail = 'Invalid currentState for request ' + respInfo.request.requestId + ', rejecting';
                        this.rejectRequest(respInfo, 'invalid-request', detail);
                        return false;
                    }
                    remoteStateOps.add(opHistory.opHash);
                }
            }
        }
        // OK - Request is valid.
        // Generate history fragment to include in the response
        const respDelta = new CausalHistoryDelta_1.CausalHistoryDelta(this.syncAgent.mutableObj, this.syncAgent.store);
        let maxHistory = req.maxHistory;
        let maxOps = req.maxLiterals;
        if (maxHistory === undefined || maxHistory > ProviderLimits.MaxHistoryPerResponse) {
            maxHistory = ProviderLimits.MaxHistoryPerResponse;
        }
        if (maxOps === undefined || maxOps > ProviderLimits.MaxOpsToRequest) {
            maxOps = ProviderLimits.MaxOpsToRequest;
        }
        let respHistoryFragment = undefined;
        if (req.requestedTerminalOpHistory !== undefined && req.requestedTerminalOpHistory.length > 0) {
            const start = req.requestedStartingOpHistory === undefined ? [] : req.requestedStartingOpHistory;
            await respDelta.compute(req.requestedTerminalOpHistory, start, maxHistory, 512);
            respHistoryFragment = respDelta.fragment.filterByTerminalOpHistories(new Set(req.requestedTerminalOpHistory));
            if (respHistoryFragment.contents.size > 0) {
                resp.history = Array.from(respHistoryFragment.contents.values()).map((h) => h.literalize());
            }
        }
        let maxLiterals = respInfo.request.maxLiterals;
        if (maxLiterals === undefined || maxLiterals > ProviderLimits.MaxLiteralsPerResponse) {
            maxLiterals = ProviderLimits.MaxLiteralsPerResponse;
        }
        // TODO: only load packer if we're going to send ops
        const packer = new ObjectPacker_1.ObjectPacker(this.syncAgent.store, maxLiterals);
        await packer.allowOmissionsRecursively(remoteStateOps.values(), 2048, this.checkIfLiteralIsValidOp);
        let full = false;
        const sendingOps = new Array();
        if (respInfo.request.requestedOps !== undefined) {
            for (const hash of respInfo.request.requestedOps) {
                if (sendingOps.length === maxOps) {
                    break;
                }
                if (!packer.allowedOmissions.has(hash)) {
                    full = !await packer.addObject(hash);
                    if (full) {
                        this.opXferLog.trace('Cannot pack ' + hash + ', no room.');
                        break;
                    }
                    else {
                        this.opXferLog.trace('Packed ' + hash + '. ' + packer.content.length + ' literals packed so far.');
                        sendingOps.push(hash);
                    }
                }
                else {
                    this.opXferLog.debug('Cannot pack ' + hash + ': it is an allowed omision.\nreference chain is: ' + packer.allowedOmissions.get(hash));
                }
            }
        }
        if (!full &&
            respInfo.request.mode === 'infer-req-ops' &&
            respInfo.request.requestedTerminalOpHistory !== undefined &&
            respHistoryFragment !== undefined &&
            sendingOps.length < maxOps) {
            const start = new Set(respInfo.request.currentState);
            const sending = new Set();
            for (const opHash of sendingOps) {
                const opHistory = respHistoryFragment.getOpHistoryForOp(opHash);
                if (opHistory !== undefined) {
                    sending.add(opHistory.causalHistoryHash);
                }
            }
            const ignore = (opHistoryHash) => sending.has(opHistoryHash);
            const extraOpsToSend = respHistoryFragment.causalClosure(start, maxOps - sendingOps.length, ignore);
            for (const opHistoryHash of extraOpsToSend) {
                const opHistory = respHistoryFragment.contents.get(opHistoryHash);
                if (!packer.allowedOmissions.has(opHistory.opHash)) {
                    full = !await packer.addObject(opHistory.opHash);
                    if (full) {
                        break;
                    }
                    else {
                        sendingOps.push(opHistory.opHash);
                    }
                }
                else {
                    this.opXferLog.debug('Omitting one inferred op due tu allowed omission: ' + opHistory.opHash);
                }
            }
        }
        // All set: send response
        if (packer.content.length > 0) {
            resp.sendingOps = sendingOps;
            resp.literalCount = packer.content.length;
            respInfo.literalsToSend = packer.content;
            respInfo.nextLiteralIdx = 0;
            if (packer.omissions.size > 0) {
                //console.log('omitting ' + packer.omissions.size + ' references');
                resp.omittedObjs = [];
                resp.omittedObjsReferenceChains = [];
                resp.omittedObjsOwnershipProofs = [];
                for (const [hash, refChain] of packer.omissions.entries()) {
                    resp.omittedObjs.push(hash);
                    resp.omittedObjsReferenceChains.push(refChain);
                    const dep = await this.syncAgent.store.load(hash);
                    resp.omittedObjsOwnershipProofs.push(dep.hash(req.omissionProofsSecret));
                }
            }
        }
        respInfo.response = resp;
        return true;
    }
    sendLiterals(requestId, maxLiterals) {
        const respInfo = this.responses.get(requestId);
        let sent = 0;
        if (respInfo !== undefined) {
            if (respInfo.literalsToSend !== undefined) {
                for (let i = 0; i < maxLiterals; i++) {
                    if (this.responses.get(requestId) === undefined) {
                        // this response is done
                        // (there could be overlap in the firing of 'sendStreamingResponses')
                        break;
                    }
                    const nextIdx = respInfo.nextLiteralIdx;
                    if (nextIdx < respInfo.literalsToSend.length) {
                        try {
                            if (!this.sendLiteral(respInfo, nextIdx, respInfo.literalsToSend[nextIdx])) {
                                break;
                            }
                        }
                        catch (e) {
                            break;
                        }
                        respInfo.nextLiteralIdx = nextIdx + 1;
                        sent = sent + 1;
                    }
                    else {
                        break;
                    }
                }
                if (this.isResponseComplete(respInfo)) {
                    this.removeResponse(respInfo);
                }
            }
        }
        //TODO: check if timer for sending should be enabled?
        return sent;
    }
    sendLiteral(respInfo, sequence, literal) {
        const msg = {
            requestId: respInfo.request.requestId,
            type: MessageType.SendLiteral,
            sequence: sequence,
            literal: literal
        };
        return this.syncAgent.sendMessageToPeer(respInfo.remote, this.syncAgent.getAgentId(), msg);
    }
    rejectRequest(respInfo, reason, detail) {
        CausalHistorySyncAgent_1.CausalHistorySyncAgent.controlLog.warning(detail);
        this.removeResponse(respInfo);
        const msg = {
            type: MessageType.RejectRequest,
            requestId: respInfo.request.requestId,
            reason: reason,
            detail: detail
        };
        this.syncAgent.sendMessageToPeer(respInfo.remote, this.syncAgent.getAgentId(), msg);
    }
    async sendResponse(respInfo) {
        var _a;
        const reqId = respInfo.request.requestId;
        this.controlLog.debug('\nSending response for ' + reqId);
        this.currentResponses.set(respInfo.remote, reqId);
        this.dequeueResponse(respInfo);
        if (await this.createResponse(respInfo)) {
            this.syncAgent.sendMessageToPeer(respInfo.remote, this.syncAgent.getAgentId(), respInfo.response);
            if (((_a = respInfo.response) === null || _a === void 0 ? void 0 : _a.literalCount) > 0) {
                this.startStreamingResponse(respInfo);
            }
            if (this.isResponseComplete(respInfo)) {
                this.removeResponse(respInfo);
            }
        }
        else {
            this.removeResponse(respInfo);
        }
    }
    attemptQueuedResponse(remote) {
        const queued = this.queuedResponses.get(remote);
        if (queued !== undefined && queued.length > 0) {
            const reqId = queued.shift();
            this.controlLog.debug('\nFound queued request ' + reqId);
            const respInfo = this.responses.get(reqId);
            this.sendResponse(respInfo);
            return true;
        }
        else {
            return false;
        }
    }
    enqueueResponse(respInfo) {
        const reqId = respInfo.request.requestId;
        this.controlLog.debug('\nEnqueuing response for ' + reqId + ' currently processing ' + this.currentResponses.get(respInfo.remote));
        let queued = this.queuedResponses.get(respInfo.remote);
        if (queued === undefined) {
            queued = [];
            this.queuedResponses.set(respInfo.remote, queued);
        }
        queued.push(reqId);
    }
    dequeueResponse(respInfo) {
        const reqId = respInfo.request.requestId;
        const queued = this.queuedResponses.get(respInfo.remote);
        const idx = queued === null || queued === void 0 ? void 0 : queued.indexOf(reqId);
        if (idx !== undefined && idx >= 0) {
            queued === null || queued === void 0 ? void 0 : queued.splice(idx);
        }
    }
    removeResponse(respInfo) {
        const requestId = respInfo.request.requestId;
        if (this.responses.get(requestId) !== undefined) {
            this.controlLog.debug('Removing sent request ' + requestId);
            this.controlLog.debug('Queue after for ' + respInfo.remote + ': ' + this.queuedResponses.get(respInfo.remote));
            // remove from current & queue
            if (this.currentResponses.get(respInfo.remote) === requestId) {
                this.currentResponses.delete(respInfo.remote);
            }
            this.dequeueResponse(respInfo);
            // remove request info
            this.responses.delete(respInfo.request.requestId);
            if (this.isStreamingResponse(respInfo)) {
                this.streamingResponses = this.streamingResponses - 1;
                if (this.streamingResponses === 0 && this.streamingResponsesInterval !== undefined) {
                    clearInterval(this.streamingResponsesInterval);
                    this.streamingResponsesInterval = undefined;
                }
            }
            const queued = this.attemptQueuedResponse(respInfo.remote);
            this.controlLog.debug('Found following request after ' + requestId + ': ' + queued);
        }
    }
    isResponseComplete(respInfo) {
        const done = (respInfo === null || respInfo === void 0 ? void 0 : respInfo.response) !== undefined &&
            (!this.isStreamingResponse(respInfo) ||
                this.isStreamingCompleted(respInfo));
        return done;
    }
    isStreamingResponse(respInfo) {
        return respInfo.literalsToSend !== undefined;
    }
    isStreamingCompleted(respInfo) {
        return respInfo.literalsToSend !== undefined &&
            respInfo.nextLiteralIdx === respInfo.literalsToSend.length;
    }
    startStreamingResponse(respInfo) {
        this.streamingResponses = this.streamingResponses + 1;
        if (this.streamingResponsesInterval === undefined) {
            this.streamingResponsesInterval = setInterval(this.continueStreamingResponses, 100);
        }
        this.sendLiterals(respInfo.request.requestId, LiteralBatchSize);
    }
    async logStoreContents(requestId) {
        if (this.storeLog.level <= logging_1.LogLevel.DEBUG) {
            this.storeLog.debug('\nStored state before response to request ' + requestId + '\n' + await this.syncAgent.lastStoredOpsDescription());
        }
    }
}
exports.CausalHistoryProvider = CausalHistoryProvider;
CausalHistoryProvider.controlLog = new logging_1.Logger(CausalHistoryProvider.name, logging_1.LogLevel.INFO);
CausalHistoryProvider.storeLog = new logging_1.Logger(CausalHistoryProvider.name, logging_1.LogLevel.INFO);
CausalHistoryProvider.opXferLog = new logging_1.Logger(CausalHistoryProvider.name, logging_1.LogLevel.INFO);
//# sourceMappingURL=CausalHistoryProvider.js.map